import type { Actuator, Joint, JointUUID, Vector3 } from "../../types/scene";
import { create } from "mutative";
import type { SceneStoreGetState, SceneStoreSetState } from "../sceneStore.types";
import type { JointActions } from "./actionTypes";
import {
  createActuatorControlPayload,
  updateGlobalActuatorMapping,
} from "../../physics/actuatorMapping";
import { mirrorActuatorPositionTargetsForJointFromScene } from "../../physics/jointLabelActuatorDrag";
import { getPhysicsWorker } from "../../physics/workerHandle";
import { usePhysicsStore } from "../physicsStore";
import { logger } from "../../utils/logger";
import { isPhysicsRuntimeAuthoringLocked } from "../../utils/physicsGuards";
import { withHistory } from "./withHistory";
import {
  isScalarJoint,
  sanitizeJointAxis,
  sanitizeJointNonNegative,
  sanitizeJointPosition,
  updateJointMapEntry,
  type JointEquality,
  type JointRecordByString,
} from "./jointActionUtils";

type JointValueActionKeys =
  | "updateJointValue"
  | "updateJointValues"
  | "updateBallJointQuat"
  | "updateJointRange"
  | "updateJointUseLimits"
  | "updateJointDamping"
  | "updateJointFrictionLoss"
  | "updateJointStiffness"
  | "updateJointAxis"
  | "updateJointPos";

export const createJointValueActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): Pick<JointActions, JointValueActionKeys> => ({
  updateJointValue: withHistory(
    (jointUuid: string, value: number) => {
      set((state) => {
        const joints = state.joints as JointRecordByString;
        const joint = joints[jointUuid];
        if (!joint) {
          logger.warn(`[updateJointValue] Joint UUID ${jointUuid} not found`);
          return state;
        }
        const [minVal, maxVal] = isScalarJoint(joint) ? joint.range : [-Math.PI, Math.PI];
        const numericValue = Number(value);
        const shouldClampToRange = joint.kind !== "slide" || joint.useLimits !== "false";
        const clampedValue = shouldClampToRange
          ? Math.max(minVal, Math.min(maxVal, numericValue))
          : numericValue;

        try {
          const physicsState = usePhysicsStore.getState();
          const physicsLocked = isPhysicsRuntimeAuthoringLocked();
          const physicsWorker: Worker | null = getPhysicsWorker();
          if (physicsLocked && physicsWorker) {
            const actuatorList: Actuator[] = Object.values(state.actuators);

            // Find actuators that control this joint directly (via joint property).
            // act.joint may be stored as the joint UUID or as the joint NAME (from parsed XML).
            const jointActuators = actuatorList.filter((act) => {
              if (!act) return false;
              const targetJoint = act.joint;
              if (!targetJoint) return false;
              return targetJoint === jointUuid || targetJoint === joint.name;
            });

            // Find actuators that control this joint via tendon
            // For tendon-based actuators, we need to check if the joint is connected to the tendon
            // This is a simplified check - in a full implementation, we'd parse tendon definitions
            // For now, we'll include all tendon-based actuators and let the physics worker handle it
            const tendonActuators = actuatorList.filter((act) => {
              if (!act || !act.tendon) return false;
              // For tendon-based actuators, we include them if they might control this joint
              // The physics worker will handle the actual mapping
              return true;
            });

            const matchingActuators = [...jointActuators, ...tendonActuators];
            const actuatorNames = matchingActuators.map((act) => act.name).filter(Boolean);
            logger.debug("[JointActions] updateJointValue", {
              jointUuid,
              jointName: joint.name,
              actuatorNames,
              jointActuators: jointActuators.map((a) => a.name),
              tendonActuators: tendonActuators.map((a) => a.name),
              isRunning: physicsState.isRunning === true,
              workerAvailable: !!physicsWorker,
            });

            // Build actuator values map using actuator names from scene store
            const actuatorValues: Record<string, number> = {};
            matchingActuators.forEach((actuator) => {
              if (!actuator?.uuid && !actuator?.name) return;
              const t = (actuator.kind || "").toLowerCase();
              if (t === "position" || t === "intposition" || t === "general") {
                // Prefer actuator UUIDs for control payloads
                const key = actuator.uuid || actuator.name;
                if (key) actuatorValues[key] = clampedValue;
              }
            });

            // Convert to control payload using mapper - this maps actuator names to control keys
            // that match the unique actuator names in the physics XML
            const jointsList = Object.values(state.joints || {});
            const bodies = state.bodies || {};
            const worldBodies = state.worldBodies || [];

            // Ensure mapping is up to date before creating payload
            // The mapping should already be built when physics starts, but ensure it's current
            try {
              updateGlobalActuatorMapping(actuatorList, jointsList, { bodies, worldBodies }, false);
            } catch (error) {
              logger.warn("[JointActions] Failed to update actuator mapping", error);
            }

            const payload = createActuatorControlPayload(actuatorValues, actuatorList);
            logger.debug("[JointActions] actuator payload", {
              actuatorNames: Object.keys(actuatorValues),
              actuatorValues,
              payload,
              payloadKeys: Object.keys(payload),
            });
            if (Object.keys(payload).length > 0) {
              physicsWorker.postMessage({ type: "controls", payload });
              logger.debug("[JointActions] postMessage sent", payload);
            } else {
              logger.warn("[JointActions] No actuators found for joint", joint.name);
            }
          }
          if (physicsLocked) {
            mirrorActuatorPositionTargetsForJointFromScene(state, jointUuid, clampedValue);
            return state;
          }
        } catch (error) {
          logger.warn("[jointActions] Failed to send joint control payload", error);
        }

        return create(state, (draft) => {
          const joints = draft.joints as JointRecordByString;
          // Only update ref for scalar joints - direct map access with UUID
          const joint = joints[jointUuid];
          if (joint && isScalarJoint(joint)) {
            joints[jointUuid] = { ...joint, ref: clampedValue };
          }

          // Joint updates are handled via jointMap; body.joints contains only UUID references

          // Check for joint equality constraints and update coupled joints
          const equalities = Object.values(draft.equality) as JointEquality[];
          equalities.forEach((eq) => {
            if (eq.type === "joint" && eq.objtype === "joint") {
              // Default polycoef is [0, 1, 0, 0, 0] if not specified (joint2 = joint1)
              const polycoef =
                eq.data && Array.isArray(eq.data) && eq.data.length > 0 ? eq.data : [0, 1, 0, 0, 0];
              let coupledJointName: string | null = null;
              let isJoint1 = false;

              // Normalize both sides for comparison
              // Check if this joint is joint1 or joint2 in the constraint
              if (eq.name1 && eq.name1 === joint.name) {
                coupledJointName = eq.name2 || null;
                isJoint1 = true;
              } else if (eq.name2 && eq.name2 === joint.name) {
                coupledJointName = eq.name1 || null;
                isJoint1 = false;
              }

              if (coupledJointName) {
                // Compute coupled joint value using polynomial: joint2 = polycoef[0] + polycoef[1]*joint1 + polycoef[2]*joint1^2 + ...
                let coupledValue: number;
                if (isJoint1) {
                  // joint2 = f(joint1)
                  coupledValue = polycoef[0] || 0;
                  let power = 1;
                  for (let i = 1; i < polycoef.length; i++) {
                    coupledValue += (polycoef[i] || 0) * Math.pow(clampedValue, power);
                    power++;
                  }
                } else {
                  // joint1 = f(joint2), so we need to solve for joint2
                  // For linear case (polycoef = [0, 1, 0, 0, 0]), joint1 = joint2, so joint2 = joint1
                  // For general case, we'd need to solve the polynomial, but for now, assume linear
                  if (polycoef.length >= 2 && polycoef[1] !== 0) {
                    // Linear: joint1 = polycoef[0] + polycoef[1] * joint2
                    // So: joint2 = (joint1 - polycoef[0]) / polycoef[1]
                    coupledValue = (clampedValue - (polycoef[0] || 0)) / polycoef[1];
                  } else {
                    // Fallback: assume identity for non-linear or zero coefficient
                    coupledValue = clampedValue;
                  }
                }

                // Clamp the coupled joint value to its range
                // Direct map lookup - no tree traversal needed
                const coupledJoint = joints[coupledJointName];
                const [coupledMin, coupledMax] =
                  coupledJoint && isScalarJoint(coupledJoint)
                    ? coupledJoint.range
                    : [-Math.PI, Math.PI];
                const clampedCoupledValue = Math.max(
                  coupledMin,
                  Math.min(coupledMax, coupledValue)
                );

                // Update the coupled joint (only for scalar joints)
                if (coupledJoint && isScalarJoint(coupledJoint)) {
                  updateJointMapEntry(joints, coupledJointName, {
                    ref: clampedCoupledValue,
                  } as Partial<Joint>);
                }

                // Joint updates are handled via jointMap; body.joints contains only UUID references

                // Dispatch event for 3D visualization update of coupled joint
                try {
                  const coupledUuid = coupledJoint?.uuid || null;
                  window.dispatchEvent(
                    new CustomEvent("joint-value-updated", {
                      detail: {
                        jointUuid: coupledUuid,
                        jointName: coupledJointName,
                        value: clampedCoupledValue,
                      },
                    })
                  );
                } catch (error) {
                  logger.warn("[jointActions] Failed to dispatch joint-value-updated event", error);
                }
              }
            }
          });
        });
      });

      try {
        const updatedJoint = (get().joints as Record<string, Joint | undefined>)[jointUuid];
        const dispatchedValue =
          updatedJoint && isScalarJoint(updatedJoint) && Number.isFinite(updatedJoint.ref)
            ? updatedJoint.ref
            : value;
        window.dispatchEvent(
          new CustomEvent("joint-value-updated", {
            detail: {
              jointUuid,
              jointName: updatedJoint?.name,
              value: dispatchedValue,
            },
          })
        );
      } catch (error) {
        logger.warn("[jointActions] Failed to dispatch joint-value-updated event", error);
      }
    },
    { allowDuringPhysics: true, actionName: "updateJointValue" }
  ),

  // Batched version of updateJointValue - updates multiple joints and sends a single control message
  // This prevents intermediate states when IK or other controllers update multiple joints at once
  updateJointValues: withHistory(
    (updates: Array<{ jointUuid: string; value: number }>) => {
      if (!updates || updates.length === 0) return;

      set((state) => {
        const joints = state.joints as JointRecordByString;
        // First, compute clamped values and collect actuator controls
        const jointUpdates: Array<{ jointUuid: string; clampedValue: number; joint: Joint }> = [];

        updates.forEach(({ jointUuid, value }) => {
          const joint = joints[jointUuid];
          if (!joint) {
            logger.warn(`[updateJointValues] Joint UUID ${jointUuid} not found`);
            return;
          }
          const [minVal, maxVal] = isScalarJoint(joint) ? joint.range : [-Math.PI, Math.PI];
          const clampedValue = Math.max(minVal, Math.min(maxVal, value));
          jointUpdates.push({ jointUuid, clampedValue, joint });
        });

        // Send a single batched control message to the physics worker
        try {
          const physicsLocked = isPhysicsRuntimeAuthoringLocked();
          const physicsWorker: Worker | null = getPhysicsWorker();
          if (physicsLocked && physicsWorker) {
            const actuatorList: Actuator[] = Object.values(state.actuators);
            const actuatorValues: Record<string, number> = {};

            jointUpdates.forEach(({ jointUuid, clampedValue, joint }) => {
              // Find actuators that control this joint.
              // act.joint may be stored as the joint UUID or as the joint NAME (from parsed XML).
              const jointName = joint?.name;
              const jointActuators = actuatorList.filter((act) => {
                if (!act) return false;
                return act.joint === jointUuid || (jointName && act.joint === jointName);
              });

              jointActuators.forEach((actuator) => {
                if (!actuator?.uuid && !actuator?.name) return;
                const t = (actuator.kind || "").toLowerCase();
                if (t === "position" || t === "intposition" || t === "general") {
                  const key = actuator.uuid || actuator.name;
                  if (key) actuatorValues[key] = clampedValue;
                }
              });
            });

            if (Object.keys(actuatorValues).length > 0) {
              // Ensure mapping is up to date
              const jointsList = Object.values(state.joints || {});
              const bodies = state.bodies || {};
              const worldBodies = state.worldBodies || [];
              try {
                updateGlobalActuatorMapping(
                  actuatorList,
                  jointsList,
                  { bodies, worldBodies },
                  false
                );
              } catch (error) {
                logger.warn("[JointActions] Failed to update actuator mapping", error);
              }

              const payload = createActuatorControlPayload(actuatorValues, actuatorList);
              if (Object.keys(payload).length > 0) {
                physicsWorker.postMessage({ type: "controls", payload });
              }
            }
          }
          if (physicsLocked) {
            jointUpdates.forEach(({ jointUuid, clampedValue }) => {
              mirrorActuatorPositionTargetsForJointFromScene(state, jointUuid, clampedValue);
            });
            return state;
          }
        } catch (error) {
          logger.warn("[jointActions] Failed to send batched joint control payload", error);
        }

        // Update the store state
        return create(state, (draft) => {
          const draftJoints = draft.joints as JointRecordByString;
          jointUpdates.forEach(({ jointUuid, clampedValue }) => {
            const joint = draftJoints[jointUuid];
            if (joint && isScalarJoint(joint)) {
              draftJoints[jointUuid] = { ...joint, ref: clampedValue };
            }
          });
        });
      });
    },
    { allowDuringPhysics: true, actionName: "updateJointValues" }
  ),

  updateBallJointQuat: withHistory((jointUuid: string, quat: [number, number, number, number]) => {
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        const joint = joints[jointUuid];
        if (joint && joint.kind === "ball") {
          joints[jointUuid] = { ...joint, quat };
        }
      })
    );
  }),

  updateJointRange: withHistory((jointUuid: string, min: number, max: number) => {
    const newMin = Math.min(min, max);
    const newMax = Math.max(min, max);

    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        // Only update range for scalar joints - direct map access with UUID
        const joint = joints[jointUuid];
        if (joint && isScalarJoint(joint)) {
          // Clamp the current value to the new range so the body stays in sync
          const currentRef = Number.isFinite(joint.ref) ? joint.ref : 0;
          const clampedRef = Math.max(newMin, Math.min(newMax, currentRef));
          joints[jointUuid] = {
            ...joint,
            range: [newMin, newMax] as [number, number],
            ref: clampedRef,
          };
        }
      })
    );
  }),

  updateJointUseLimits: withHistory((jointUuid: string, useLimits: "auto" | "true" | "false") => {
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        const joint = joints[jointUuid];
        if (!joint || joint.kind === "free") return;
        if (joint.kind === "hinge" || joint.kind === "slide" || joint.kind === "ball") {
          joints[jointUuid] = { ...joint, useLimits };
        }
      })
    );
  }),

  updateJointDamping: withHistory((jointUuid: string, damping: number) => {
    const nextDamping = sanitizeJointNonNegative(damping);
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        const joint = joints[jointUuid];
        if (joint) {
          joints[jointUuid] = { ...joint, damping: nextDamping };
        }
      })
    );
  }),

  updateJointFrictionLoss: withHistory((jointUuid: string, frictionloss: number) => {
    const nextFriction = sanitizeJointNonNegative(frictionloss);
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        const joint = joints[jointUuid];
        if (joint) {
          joints[jointUuid] = { ...joint, frictionloss: nextFriction };
        }
      })
    );
  }),

  updateJointStiffness: withHistory((jointUuid: string, stiffness: number) => {
    const nextStiffness = sanitizeJointNonNegative(stiffness);
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        const joint = joints[jointUuid];
        if (joint) {
          joints[jointUuid] = { ...joint, stiffness: nextStiffness };
        }
      })
    );
  }),

  updateJointAxis: withHistory((jointUuid: string, axis: Vector3) => {
    const safeAxis = sanitizeJointAxis(axis);
    if (!safeAxis) {
      logger.warn(
        "[jointActions] Rejected joint axis update: axis must be a non-zero finite vector."
      );
      return;
    }
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        // Only update axis for scalar joints - direct map access with UUID
        const joint = joints[jointUuid];
        if (joint && isScalarJoint(joint)) {
          joints[jointUuid] = { ...joint, axis: safeAxis };
        }

        // Joint updates are handled via jointMap; body.joints contains only UUID references
      })
    );

    // Dispatch event for 3D visualization update
    const jointName = get().joints[jointUuid as JointUUID]?.name;
    try {
      window.dispatchEvent(
        new CustomEvent("joint-axis-updated", {
          detail: { jointUuid, jointName, axis: safeAxis },
        })
      );
    } catch (error) {
      logger.warn("[jointActions] Failed to dispatch joint-axis-updated event", error);
    }
  }),

  updateJointPos: withHistory((jointUuid: string, position: Vector3) => {
    const safePos = sanitizeJointPosition(position);
    set((state) =>
      create(state, (draft) => {
        const joints = draft.joints as JointRecordByString;
        // Position exists on BallJoint, HingeJoint, and SlideJoint - direct map access with UUID
        const joint = joints[jointUuid];
        if (joint && (joint.kind === "ball" || isScalarJoint(joint))) {
          joints[jointUuid] = { ...joint, position: safePos };
        }

        // Joint updates are handled via jointMap; body.joints contains only UUID references

        const jointName = joint?.name;
        try {
          window.dispatchEvent(
            new CustomEvent("joint-pos-updated", {
              detail: { jointUuid, jointName, pos: safePos },
            })
          );
        } catch (error) {
          logger.warn("[jointActions] Failed to dispatch joint-pos-updated event", error);
        }
      })
    );
  }),
});
